import { Trash2 } from "lucide-react";
import { eur } from "@/lib/api";

const fmt = (v) => new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

function Chip({ e, canDelete, onDelete, tone }) {
  return (
    <span className="group relative inline-flex" data-testid={`cash-chip-${e.id}`}>
      <button type="button" className={`font-mono text-sm font-semibold px-2.5 py-1 rounded-lg border ${tone} ${e.note ? "underline decoration-dotted underline-offset-4" : ""}`} aria-label={`${fmt(e.amount)} euro ore ${e.time}${e.note ? `, nota ${e.note}` : ""}`}>
        {fmt(e.amount)}
      </button>
      <span className="pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[220px] rounded-xl bg-[var(--ink)] text-white text-xs p-2.5 shadow-xl" data-testid={`cash-chip-tooltip-${e.id}`}>
        <span className="block font-semibold">ore {e.time}</span>
        {e.note && <span className="block">nota: {e.note}</span>}
        <span className="block text-zinc-400">{e.created_by}</span>
        {canDelete && (
          <button type="button" onClick={() => onDelete(e)} className="mt-1.5 inline-flex items-center gap-1 text-red-300 hover:text-red-200" data-testid={`cash-delete-${e.id}`}>
            <Trash2 className="h-3 w-3" /> Elimina
          </button>
        )}
      </span>
    </span>
  );
}

export function CashLine({ title, entries, tone, user, onDelete, testid }) {
  const total = entries.reduce((s, e) => s + e.amount, 0);
  return (
    <div className="rounded-2xl bg-white border border-[var(--border)] p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="eyebrow">{title}</p>
        <p className="font-mono font-bold text-lg" data-testid={`${testid}-total`}>{eur(total)}</p>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-[var(--muted)]">Nessun movimento</p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {entries.map((e, i) => (
            <span key={e.id} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="text-[var(--muted)] font-mono">+</span>}
              <Chip e={e} tone={tone} canDelete={user.role === "admin" || e.created_by_id === user.id} onDelete={onDelete} />
            </span>
          ))}
          <span className="font-mono text-[var(--muted)] ml-1">= {fmt(total)}</span>
        </div>
      )}
    </div>
  );
}

export function cashTotals(entries) {
  const s = { pos: 0, contanti: 0, spesa: 0 };
  entries.forEach((e) => { s[e.kind] += e.amount; });
  return { ...s, incassi: s.pos + s.contanti, netto: s.pos + s.contanti - s.spesa };
}

export function CashTotals({ entries, label = "Totale incassato oggi" }) {
  const t = cashTotals(entries);
  return (
    <div className="rounded-2xl bg-[var(--ink)] text-white p-6 space-y-4" data-testid="cash-totals">
      <div>
        <p className="text-sm text-zinc-400">{label}</p>
        <p className="font-mono text-4xl font-bold" data-testid="cash-total-incassi">{eur(t.incassi)}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-4 text-sm">
        <div><p className="text-zinc-400">POS</p><p className="font-mono text-lg text-sky-300" data-testid="cash-total-pos">{eur(t.pos)}</p></div>
        <div><p className="text-zinc-400">Contanti</p><p className="font-mono text-lg text-emerald-300" data-testid="cash-total-contanti">{eur(t.contanti)}</p></div>
        <div><p className="text-zinc-400">Spese negozio</p><p className="font-mono text-lg text-red-300" data-testid="cash-total-spese">- {eur(t.spesa)}</p></div>
        <div><p className="text-zinc-400">Netto</p><p className="font-mono text-lg font-bold" data-testid="cash-total-netto">{eur(t.netto)}</p></div>
      </div>
    </div>
  );
}
